import { Routes, Route, useNavigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainApp from './MainApp';
import { supabase } from './supabase';
const LandingGate = lazy(() => import('./LandingGate'));

// ── Lazy loaded portals ───────────────────────────────────────────────────
const StudentPublicProfile   = lazy(() => import('./StudentPublicProfile'));
const JobBoard               = lazy(() => import('./JobBoard'));
const JobSEOPage             = lazy(() => import('./JobSEOPage'));
const AdminDashboard         = lazy(() => import('./AdminDashboard'));
const OJTBlogGenerator       = lazy(() => import('./OJTBlogGenerator'));
const PaymentPage            = lazy(() => import('./PaymentPage'));
const AICareerCoach          = lazy(() => import('./AICareerCoach'));
const OJTCertificate         = lazy(() => import('./OJTCertificate'));
const OJTLogbook             = lazy(() => import('./OJTLogbook'));
const OJTProgressPage        = lazy(() => import('./OJTProgress'));
const DocumentCenter         = lazy(() => import('./DocumentCenter'));
const UniversityPortal       = lazy(() => import('./UniversityPortal'));
const UniversityAdminDashboard = lazy(() => import('./UniversityAdminDashboard'));
const UniversitySignup       = lazy(() => import('./UniversitySignup'));
const EmployerDashboard      = lazy(() => import('./EmployerDashboard'));
const CoordinatorMobileApp   = lazy(() => import('./CoordinatorMobileApp'));
const MOAGenerator           = lazy(() => import('./MOAGenerator'));
const LegalPage              = lazy(() => import('./LegalPage'));
const NotFoundPage           = lazy(() => import('./NotFoundPage'));
const BlogListPage           = lazy(() => import('./BlogPage').then(m => ({ default: m.BlogListPage })));
const BlogPostPage           = lazy(() => import('./BlogPage').then(m => ({ default: m.BlogPostPage })));
const CertificateVerifyPage  = lazy(() => import('./OJTCertificate').then(m => ({ default: m.CertificateVerifyPage })));

// ── Chat Widget ───────────────────────────────────────────────────────────
const ChatWidget = lazy(() => import('./ChatWidget'));

function PageLoader() {
  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center',
      justifyContent:'center', background:'#f7f9f8' }}>
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:16 }}>
        <img src="/OJT_logo.png" alt="OJT Hub"
          style={{ height:56, objectFit:'contain', opacity:0.7 }}/>
        <div style={{ width:32, height:32, border:'3px solid #e2e8e5',
          borderTopColor:'#0a6e4e', borderRadius:'50%',
          animation:'spin .7s linear infinite' }}/>
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    </div>
  );
}

function PaymentWrapper() {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<PageLoader />}>
      <PaymentPage onBack={() => navigate('/')} onSuccess={() => navigate('/')} />
    </Suspense>
  );
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/*
          IMPORTANT: / and /app/* both render the SAME MainApp instance.
          MainApp handles everything internally:
          - No session → shows LandingPage (page="landing")
          - Has session → shows dashboard (page="home")
          - Never redirects between / and /app — stays on same URL
          This eliminates the interchange/flicker issue entirely.
        */}
        {/* / = Landing page only. /app = Dashboard only. No sharing. */}
        <Route path="/"      element={<LandingGate />} />
        <Route path="/app"   element={<MainApp />} />
        <Route path="/app/*" element={<MainApp />} />

        {/* Public job board */}
        <Route path="/jobs"      element={<JobBoard />} />
        <Route path="/job/:slug" element={<JobSEOPage />} />

        {/* Portals */}
        <Route path="/employer"           element={<EmployerDashboard />} />
        <Route path="/university"         element={<UniversityPortal />} />
        <Route path="/university-admin"   element={<UniversityAdminDashboard />} />
        <Route path="/university-signup"  element={<UniversitySignup />} />
        <Route path="/coordinator-mobile" element={<CoordinatorMobileApp />} />
        <Route path="/admin"              element={<AdminDashboard />} />
        <Route path="/admin/blog-generator" element={<OJTBlogGenerator />} />

        {/* Tools */}
        <Route path="/coach"       element={<AICareerCoach />} />
        <Route path="/certificate" element={<OJTCertificate />} />
        <Route path="/logbook"     element={<OJTLogbook />} />
        <Route path="/progress"    element={<OJTProgressPage />} />
        <Route path="/documents"   element={<DocumentCenter />} />
        <Route path="/moa"         element={<MOAGenerator />} />
        <Route path="/pricing"     element={<PaymentWrapper />} />

        {/* Verify */}
        <Route path="/verify"         element={<CertificateVerifyPage />} />
        <Route path="/verify/:certId" element={<CertificateVerifyPage />} />

        {/* Content */}
        <Route path="/blog"       element={<BlogListPage supabase={supabase} />} />
<Route path="/blog/:slug" element={<BlogPostPage supabase={supabase} />} />
        <Route path="/privacy"    element={<LegalPage type="privacy" />} />
        <Route path="/terms"      element={<LegalPage type="terms" />} />

        {/* Student public profile */}
        <Route path="/student/:id" element={<StudentPublicProfile />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* OJT Hub Chat Widget — floats on every page */}
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>

    </Suspense>
  );
}
